import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Btn, Table, Td, Empty, Badge } from "../components/UI";
import { SlidePanel, ConfirmDialog } from "../components/SlidePanel";
import { FLabel, FInput, FSelect, FTextarea } from "./engagement/FormControls";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/permissions";
import { usePagination, PaginationBar } from "../components/Pagination";
import { Plus, Building2, Search, Pencil, Archive, ArchiveRestore, X, Check } from "lucide-react";
import { toast } from "sonner";

export const PAYER_TYPES = ["Commercial", "Medicaid", "Medicaid MCO", "Medicare", "Medicare Advantage", "Tricare / VA", "Workers' Compensation", "EAP", "Self-Pay / Other"];
export const PLAN_TYPES = ["PPO", "HMO", "EPO", "POS", "Managed Care", "Fee-for-Service", "Indemnity", "Other"];
export const NETWORK_STATUSES = ["In-Network", "Out-of-Network", "Participating", "Credentialing in progress", "Not contracted"];
const SUBMISSION_METHODS = ["Electronic (EDI 837P)", "Electronic (EDI 837I)", "Payer Portal", "Paper (CMS-1500)", "Paper (UB-04)"];
const FILING_DAYS = ["30", "60", "90", "120", "180", "365"];

const EMPTY = {
  name: "", payer_id: "", electronic_payer_id: "", payer_type: "", plan_type: "", network_status: "",
  phone: "", fax: "", email: "", website: "", provider_relations_phone: "",
  address: "", city: "", state: "", zip: "", contact_person: "",
  submission_method: "", clearinghouse: "", payer_portal_url: "",
  claims_address: "", claims_city: "", claims_state: "", claims_zip: "",
  requires_authorization: "", auth_phone: "", auth_fax: "", eligibility_phone: "",
  timely_filing_days: "", era_enrolled: "", eft_enabled: "", notes: "",
};

const FSection = ({ title, children }) => (
  <div className="rounded-xl border border-[#F1E8DA] bg-[#FDFCF8] p-4">
    <div className="font-heading text-sm font-bold text-[#3E2723] mb-3">{title}</div>
    <div className="grid grid-cols-2 gap-x-4 gap-y-3">{children}</div>
  </div>
);

const YESNO = [["", "-"], ["yes", "Yes"], ["no", "No"]];

export function CompanyForm({ form, setForm, onSubmit, onCancel, busy, editId }) {
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const opts = (list) => list.map((o) => <option key={o} value={o}>{o}</option>);
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <FSection title="Company Details">
        <div className="col-span-2"><FLabel>Company Name *</FLabel><FInput data-testid="company-name-input" value={form.name} onChange={f("name")} required /></div>
        <div><FLabel>Payer ID</FLabel><FInput data-testid="company-payer-input" value={form.payer_id} onChange={f("payer_id")} /></div>
        <div><FLabel>Electronic Payer ID (EDI)</FLabel><FInput data-testid="company-edi-input" value={form.electronic_payer_id} onChange={f("electronic_payer_id")} /></div>
        <div><FLabel>Payer Type</FLabel><FSelect data-testid="company-payer-type" value={form.payer_type} onChange={f("payer_type")}><option value="">-</option>{opts(PAYER_TYPES)}</FSelect></div>
        <div><FLabel>Plan Type</FLabel><FSelect data-testid="company-plan-type" value={form.plan_type} onChange={f("plan_type")}><option value="">-</option>{opts(PLAN_TYPES)}</FSelect></div>
        <div className="col-span-2"><FLabel>Network Status</FLabel><FSelect data-testid="company-network-status" value={form.network_status} onChange={f("network_status")}><option value="">-</option>{opts(NETWORK_STATUSES)}</FSelect></div>
      </FSection>
      <FSection title="Contact Information">
        <div><FLabel>Contact Person</FLabel><FInput data-testid="company-contact-input" value={form.contact_person} onChange={f("contact_person")} /></div>
        <div><FLabel>Main Phone</FLabel><FInput value={form.phone} onChange={f("phone")} /></div>
        <div><FLabel>Fax</FLabel><FInput value={form.fax} onChange={f("fax")} /></div>
        <div><FLabel>Provider Relations Phone</FLabel><FInput value={form.provider_relations_phone} onChange={f("provider_relations_phone")} /></div>
        <div><FLabel>Email</FLabel><FInput type="email" value={form.email} onChange={f("email")} /></div>
        <div><FLabel>Website</FLabel><FInput value={form.website} onChange={f("website")} /></div>
        <div className="col-span-2"><FLabel>Street Address</FLabel><FInput value={form.address} onChange={f("address")} /></div>
        <div><FLabel>City</FLabel><FInput value={form.city} onChange={f("city")} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><FLabel>State</FLabel><FInput value={form.state} onChange={f("state")} /></div>
          <div><FLabel>ZIP</FLabel><FInput value={form.zip} onChange={f("zip")} /></div>
        </div>
      </FSection>
      <FSection title="Claims & Submission">
        <div><FLabel>Submission Method</FLabel><FSelect data-testid="company-submission-method" value={form.submission_method} onChange={f("submission_method")}><option value="">-</option>{opts(SUBMISSION_METHODS)}</FSelect></div>
        <div><FLabel>Clearinghouse</FLabel><FInput value={form.clearinghouse} onChange={f("clearinghouse")} /></div>
        <div className="col-span-2"><FLabel>Payer Portal URL</FLabel><FInput value={form.payer_portal_url} onChange={f("payer_portal_url")} /></div>
        <div className="col-span-2"><FLabel>Claims Mailing Address</FLabel><FInput value={form.claims_address} onChange={f("claims_address")} /></div>
        <div><FLabel>Claims City</FLabel><FInput value={form.claims_city} onChange={f("claims_city")} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><FLabel>State</FLabel><FInput value={form.claims_state} onChange={f("claims_state")} /></div>
          <div><FLabel>ZIP</FLabel><FInput value={form.claims_zip} onChange={f("claims_zip")} /></div>
        </div>
      </FSection>
      <FSection title="Authorizations & Eligibility">
        <div><FLabel>Requires Prior Authorization</FLabel><FSelect data-testid="company-requires-auth" value={form.requires_authorization} onChange={f("requires_authorization")}>{YESNO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</FSelect></div>
        <div><FLabel>Eligibility Phone</FLabel><FInput value={form.eligibility_phone} onChange={f("eligibility_phone")} /></div>
        <div><FLabel>Authorization Phone</FLabel><FInput value={form.auth_phone} onChange={f("auth_phone")} /></div>
        <div><FLabel>Authorization Fax</FLabel><FInput value={form.auth_fax} onChange={f("auth_fax")} /></div>
      </FSection>
      <FSection title="Billing Configuration">
        <div><FLabel>Timely Filing (days)</FLabel><FSelect data-testid="company-timely-filing" value={form.timely_filing_days} onChange={f("timely_filing_days")}><option value="">-</option>{opts(FILING_DAYS)}</FSelect></div>
        <div><FLabel>ERA Enrolled</FLabel><FSelect value={form.era_enrolled} onChange={f("era_enrolled")}>{YESNO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</FSelect></div>
        <div><FLabel>EFT Enabled</FLabel><FSelect value={form.eft_enabled} onChange={f("eft_enabled")}>{YESNO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</FSelect></div>
      </FSection>
      <div><FLabel>Notes</FLabel><FTextarea data-testid="company-notes-input" rows={2} value={form.notes} onChange={f("notes")} /></div>
      <div className="sticky bottom-0 -mx-6 -mb-5 mt-4 flex justify-end gap-2 border-t border-[#F1EEE7] bg-white px-6 py-4">
        <Btn type="button" variant="ghost" onClick={onCancel}>Cancel</Btn>
        <Btn data-testid="company-save-btn" type="submit" disabled={busy || !form.name.trim()}>{busy ? "Saving…" : editId ? "Save Changes" : "Add Company"}</Btn>
      </div>
    </form>
  );
}

const CheckBox = ({ checked, onChange, testId, label }) => (
  <button type="button" data-testid={testId} aria-label={label} onClick={onChange}
    className={`flex h-5 w-5 items-center justify-center rounded-md border transition-colors ${checked ? "bg-[#4E342E] border-[#4E342E]" : "bg-white border-[#C9BEB0] hover:border-[#8D6E63]"}`}>
    {checked && <Check size={13} className="text-white" />}
  </button>
);

export default function InsuranceCompanies() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canBilling = canEdit(user, "billing");
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [view, setView] = useState("active");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [panel, setPanel] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // {action}

  const load = () => api.get("/insurance-companies").then((r) => setCompanies(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { setSelected(new Set()); }, [view]);

  const filtered = useMemo(() => companies
    .filter((c) => (view === "all" ? true : (c.status || "active") === view))
    .filter((c) => `${c.name} ${c.payer_id} ${c.payer_type}`.toLowerCase().includes(search.toLowerCase())), [companies, view, search]);

  const allSelected = filtered.length > 0 && filtered.every((c) => selected.has(c.id));
  const pg = usePagination(filtered);
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(filtered.map((c) => c.id)));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const openAdd = () => { setForm(EMPTY); setEditId(null); setPanel(true); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, c[k] || ""])) }); setEditId(c.id); setPanel(true); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) await api.put(`/insurance-companies/${editId}`, form);
      else await api.post("/insurance-companies", form);
      toast.success(editId ? "Company updated" : "Insurance company added");
      setPanel(false); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const bulkAction = async () => {
    setBusy(true);
    try {
      const r = await api.post("/insurance-companies/bulk-status", { ids: [...selected], action: confirm.action });
      toast.success(`${r.data.modified} compan${r.data.modified === 1 ? "y" : "ies"} ${confirm.action === "archive" ? "archived" : "restored"}`);
      setConfirm(null); setSelected(new Set()); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  return (
    <div>
      <PageHeader title="Insurance Companies" subtitle="Payer directory, claims & authorization configuration"
        action={canBilling && <Btn data-testid="add-company-btn" onClick={openAdd}><Plus size={16} /> New Company</Btn>} />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex gap-2">
          {["active", "archived", "all"].map((v) => (
            <button key={v} data-testid={`companies-view-${v}`} onClick={() => setView(v)}
              className={`rounded-full px-4 py-2 text-sm font-semibold capitalize transition-colors ${view === v ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>
              {v} <span className="ml-1 text-xs opacity-70">{companies.filter((c) => v === "all" || (c.status || "active") === v).length}</span>
            </button>
          ))}
        </div>
        <div className="relative ml-auto">
          <Search size={15} className="absolute left-3 top-3 text-[#84675B]" />
          <FInput data-testid="companies-search" placeholder="Search payers…" className="pl-9 h-10 w-64" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {isAdmin && selected.size > 0 && (
        <div data-testid="bulk-action-bar" className="mb-3 flex flex-wrap items-center gap-3 rounded-xl bg-[#3E2723] px-4 py-2.5 text-white shadow-lg">
          <span className="text-sm font-semibold" data-testid="bulk-selected-count">{selected.size} selected</span>
          <span className="h-4 w-px bg-white/20" />
          {view !== "archived" && (
            <button type="button" data-testid="bulk-archive-btn" onClick={() => setConfirm({ action: "archive" })}
              className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-white/10 transition-colors">
              <Archive size={14} /> Archive
            </button>
          )}
          {view !== "active" && (
            <button type="button" data-testid="bulk-restore-btn" onClick={() => setConfirm({ action: "restore" })}
              className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-white/10 transition-colors">
              <ArchiveRestore size={14} /> Restore
            </button>
          )}
          <button type="button" data-testid="bulk-clear-btn" aria-label="Clear selection" onClick={() => setSelected(new Set())}
            className="ml-auto flex h-7 w-7 items-center justify-center rounded-full hover:bg-white/10"><X size={14} /></button>
        </div>
      )}

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead><tr>
              {isAdmin && <th className="bg-[#FDFCF8] border-y border-[#EFEBE0] py-3 px-4 w-10"><CheckBox checked={allSelected} onChange={toggleAll} testId="select-all-companies" label="Select all" /></th>}
              {["Company", "Payer ID", "Type", "Network", "Phone", "Filing (days)", "Status", ""].map((h) => (
                <th key={h} className="bg-[#FDFCF8] border-y border-[#EFEBE0] text-[#4E342E] font-semibold py-3 px-4 whitespace-nowrap">{h}</th>))}
            </tr></thead>
            <tbody>
              {pg.pageRows.map((c) => (
                <tr key={c.id} data-testid={`company-row-${c.id}`} className={`hover:bg-[#FDFCF8] ${selected.has(c.id) ? "bg-[#F5F2EB]" : ""}`}>
                  {isAdmin && <td className="border-b border-[#EFEBE0] py-3 px-4"><CheckBox checked={selected.has(c.id)} onChange={() => toggle(c.id)} testId={`select-company-${c.id}`} label={`Select ${c.name}`} /></td>}
                  <Td className="font-semibold cursor-pointer" onClick={() => navigate(`/insurance-companies/${c.id}`)}>
                    <span className="inline-flex items-center gap-2"><Building2 size={15} className="text-[#7B6157]" /> {c.name}</span>
                  </Td>
                  <Td>{c.payer_id}</Td>
                  <Td>{c.payer_type || "-"}</Td>
                  <Td className="whitespace-nowrap">{c.network_status || "-"}</Td>
                  <Td className="whitespace-nowrap">{c.phone}</Td>
                  <Td>{c.timely_filing_days ? `${c.timely_filing_days}d` : "-"}</Td>
                  <Td><Badge value={c.status || "active"} /></Td>
                  <Td>
                    {canBilling && <button type="button" data-testid={`edit-company-${c.id}`} aria-label={`Edit ${c.name}`} onClick={() => openEdit(c)}
                      className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Pencil size={13} /></button>}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          <PaginationBar {...pg} testId="companies-pagination" />
        </div>
        {filtered.length === 0 && <Empty text={search ? "No payers match your search" : `No ${view === "all" ? "" : view} insurance companies`} />}
      </Card>

      <SlidePanel open={panel} onClose={() => setPanel(false)} title={editId ? "Edit Insurance Company" : "New Insurance Company"}
        subtitle="Payer identity, claims routing, authorizations & billing configuration" width="max-w-2xl" testId="company-slide-panel">
        <CompanyForm form={form} setForm={setForm} onSubmit={save} onCancel={() => setPanel(false)} busy={busy} editId={editId} />
      </SlidePanel>

      <ConfirmDialog open={!!confirm} onClose={() => setConfirm(null)} onConfirm={bulkAction} busy={busy}
        title={confirm?.action === "archive" ? `Archive ${selected.size} compan${selected.size === 1 ? "y" : "ies"}` : `Restore ${selected.size} compan${selected.size === 1 ? "y" : "ies"}`}
        confirmWord={confirm?.action === "archive" ? "ARCHIVE" : "RESTORE"}
        confirmLabel={confirm?.action === "archive" ? "Archive" : "Restore"}
        warning={confirm?.action === "archive"
          ? "Archived payers are hidden from active lists and pickers but keep all history. You can restore them anytime."
          : "Restored payers become available again in active lists and pickers."}
        description={confirm ? [...selected].slice(0, 4).map((id) => companies.find((c) => c.id === id)?.name).filter(Boolean).join(", ") + (selected.size > 4 ? ` +${selected.size - 4} more` : "") : ""} />
    </div>
  );
}
